import { useEffect, useState } from "react";
import { ApolloError } from "@apollo/client";
import { FormikProps, useFormik } from "formik";
import { useSession } from "next-auth/react";
import * as Yup from "yup";
import { useOptIn } from "src/hooks/useOptIn";
import { useSegment } from "src/hooks/useSegment";
import { updateSession } from "src/utils/updateSession";
import { AuthProvider } from "src/pages/api/auth/getUserTokenFromApi";
import FORM_ERROR_MESSAGES from "src/messages/form";
import { Origin } from "src/types/graphql.d";

interface OptInFormValues {
  email: string;
}

interface UseOptInFormArgs {
  origin: Origin;
  trackingOrigin: string;
  initialEmail?: string | null;
}

interface UseOptInForm {
  formik: FormikProps<OptInFormValues>;
  submitted: boolean;
  error: ApolloError | undefined;
  loading: boolean;
}

function useOptInForm({
  origin,
  trackingOrigin,
  initialEmail,
}: UseOptInFormArgs): UseOptInForm {
  const { data: session } = useSession();
  const signInPlatform = session?.user.signInPlatform;
  const { user, segmentOptIn } = useSegment();

  const [submitted, setSubmitted] = useState(false);
  const [hasEdited, setHasEdited] = useState(false);

  const {
    optIn,
    loading,
    error: optInError,
  } = useOptIn({
    onCompleted: (data) => {
      if (!data.optIn) {
        return;
      }

      setSubmitted(true);

      if (data.optIn.token && signInPlatform !== AuthProvider.GUEST_LOGIN) {
        void updateSession({ token: data.optIn.token });
      }
    },
  });

  const formik: FormikProps<OptInFormValues> = useFormik<OptInFormValues>({
    enableReinitialize: !hasEdited,
    initialValues: {
      email: initialEmail || "",
    },
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .required(FORM_ERROR_MESSAGES.emailRequired)
        .email(FORM_ERROR_MESSAGES.email),
    }),
    onSubmit: async (values) => {
      const htUser = await Promise.resolve(user);

      void optIn({
        email: values.email,
        segment_anonymous_id: htUser?.anonymousId?.() ?? "",
        metadata: { origin },
      });
      void segmentOptIn(true, values.email, trackingOrigin);
    },
  });

  useEffect(() => {
    if (formik.dirty) {
      setHasEdited(true);
    }
  }, [formik.dirty]);

  return {
    formik,
    submitted,
    error: optInError,
    loading,
  };
}

export { useOptInForm };
export type { OptInFormValues };
